import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, forkJoin, switchMap, tap } from 'rxjs';
import { BaseComponent } from '../components/base/base.component';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientBillingService {
  private apiUrl = `api/coding/patient`;
  private patientBillingSubject = new BehaviorSubject<any[] | null>(null);
  private editingBillingSubject = new BehaviorSubject<any>(null);

  patientBilling$ = this.patientBillingSubject.asObservable();
  editingBilling$ = this.editingBillingSubject.asObservable();

  patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          this.patientBillingSubject.next([]);
          return this.loadBillingByPatient(patient.id);
        } else {
          this.patientBillingSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const codes = results ? results.data : [];
        this.patientBillingSubject.next(codes);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    // Single code events (legacy support)
    this.socketService.listen('billingAdded', 'patient-billing-service').subscribe(code => {
      if (code.patientId === currentPatientId) {
        const currentCodes = this.patientBillingSubject.getValue() || [];
        this.patientBillingSubject.next([...currentCodes!, code]);
      }
    });

    this.socketService.listen('billingUpdated', 'patient-billing-service').subscribe(updated => {
      if (updated.patientId === currentPatientId) {
        const current = this.patientBillingSubject.getValue() || [];
        const updatedItems = current!.map(item => item.id === updated.id ? updated : item);
        this.patientBillingSubject.next(updatedItems);
      }
    });

    this.socketService.listen('billingDeleted', 'patient-billing-service').subscribe(deleted => {
      if (deleted.patientId === currentPatientId) {
        const current = this.patientBillingSubject.getValue() || [];
        const updatedItems = current!.filter(item => item.id !== deleted.id);
        this.patientBillingSubject.next(updatedItems);
      }
    });

    // Batch events (preferred for performance)
    this.socketService.listen('billingBatchAdded', 'patient-billing-service').subscribe(batchData => {
      if (batchData.patientId === currentPatientId) {
        const currentCodes = this.patientBillingSubject.getValue() || [];
        this.patientBillingSubject.next([...currentCodes!, ...batchData.codes]);
      }
    });

    this.socketService.listen('billingBatchUpdated', 'patient-billing-service').subscribe(batchData => {
      if (batchData.patientId === currentPatientId) {
        const current = this.patientBillingSubject.getValue() || [];
        const updated = [...current];
        
        // Update each code in the batch
        batchData.codes.forEach((updatedCode: any) => {
          const index = updated.findIndex(item => item.id === updatedCode.id);
          if (index !== -1) {
            updated[index] = updatedCode;
          }
        });
        
        this.patientBillingSubject.next(updated);
      }
    });

    this.socketService.listen('billingBatchDeleted', 'patient-billing-service').subscribe(batchData => {
      if (batchData.patientId === currentPatientId) {
        const current = this.patientBillingSubject.getValue() || [];
        // Remove all deleted codes from the current list
        const deletedIds = batchData.codes.map((code: any) => code.id);
        const updatedItems = current!.filter(item => !deletedIds.includes(item.id));
        this.patientBillingSubject.next(updatedItems);
      }
    });

  }

  loadBillingByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  saveCodeGroup(data: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/code-group/`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getProcedureCodesByDate(date: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${this.patientId}/date/${date}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateCode(id: string, noteData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/code/${id}`, noteData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteCode(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/code/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingCode(code: any | null) {
    this.editingBillingSubject.next(code);
  }

  private doesCodeExistForDate(code: string, date: string, patientProcedureCodes: any[] | null): boolean {
    return patientProcedureCodes?.some(p => p.date === date && p.code === code) || false;
  };

  async checkMissingCoding(
    patient: any,
    formValues: any,
    hospitals: any[],
    patientProcedureCodes: any[] | null,
    base: BaseComponent,
    checkAdmissionTypeCoding: boolean = true
  ): Promise<boolean> {
    const { status, admissionDate, hospitalId, wardId, admissionType } = formValues;

    // Only continue for inpatients
    if (status === 'opd') {
      return false;
    }

    // Only continue if the selected ward has auto coding codes
    const selectedHospital = hospitals.find((hospital: any) => hospital.id === hospitalId);
    if (!selectedHospital) {
      return false;
    }

    const selectedWard = selectedHospital.wards.find((ward: any) => ward.id === wardId);
    if (!selectedWard || !selectedWard.procedureCodes || selectedWard.procedureCodes.length === 0) {
      return false;
    }

    const procedureCodes = selectedWard.procedureCodes;
    const admissionMoment = moment.utc(admissionDate).startOf('day');
    const yesterdayMoment = moment.utc().startOf('day').subtract(1, 'days');
    const datesList: string[] = [];

    let admissionCodes = [];
    let admissionCodingMessage = '';

    // Check for emergency admission
    if (admissionType === 'Emergency Admission') {
      admissionCodes = [
        {
          code: "0147",
          description: "For an unscheduled emergency consultation/visit away from the doctor's home or rooms, all hours: ADD only to the consultation/visit items 0190-0192, items 0173-0175, items 0161-0164 or items 0166-0169, as appropriate. Note: Only one of items 0145, 0146 or items 0147 may be charged and not combinations thereof",
          units: "0.00",
          type: "consultation",
          tags: "All Specialties"
        },
        {
          code: "0174",
          description: "First hospital consultation of moderately above average duration. Includes counselling with the patient and/or family and co-ordination with other health care providers or liaison with third parties on behalf of the patient",
          units: "0.00",
          type: "consultation",
          tags: "All Specialties"
        }
      ];
    } else {
      admissionCodes = [
        {
          code: "0174",
          description: "First hospital consultation of moderately above average duration. Includes counselling with the patient and/or family and co-ordination with other health care providers or liaison with third parties on behalf of the patient",
          units: "0.00",
          type: "consultation",
          tags: "All Specialties"
        }
      ];
    }
    admissionCodingMessage = `Do you want to apply the following admission codes to the admission date?\nCodes: ${admissionCodes.map(c => c.code).join(', ')}\n`;

    // Only continue if admission date is not today
    if (moment.utc().isSame(admissionMoment, 'day')) {

      if (checkAdmissionTypeCoding) {
        const confirmed = await base.showConfirmationDialog('Apply Admission Coding?', admissionCodingMessage);
        if (confirmed) {
          await this.saveMissedProcedureCodes(patient, [admissionMoment.format('DD/MM/YYYY')], admissionCodes, base);
        }
      }

      return false;
    }

    // Compile a list of dates starting at the admission date and leading up to yesterday
    for (let m = admissionMoment.clone(); m.isSameOrBefore(yesterdayMoment); m.add(1, 'days')) {
      datesList.push(m.format('DD/MM/YYYY'));
    }

    const filteredDatesList = datesList.filter(date => {
      return !procedureCodes.some((code: any) => this.doesCodeExistForDate(code.code, date, patientProcedureCodes));
    });

    const dateRange = `${admissionMoment.format('DD/MM/YYYY')} - ${yesterdayMoment.format('DD/MM/YYYY')}`;
    const filteredDateRange = filteredDatesList.length > 0
      ? `${admissionMoment.format('DD/MM/YYYY')} - ${filteredDatesList[filteredDatesList.length - 1]}`
      : dateRange;
    const confirmMessage = `Do you want to apply the following ward codes to the following date range?\nCodes: ${procedureCodes.map((c: any) => c.code).join(', ')}\nDate Range: ${filteredDateRange}`;
    const combinedMessage = (checkAdmissionTypeCoding ? (admissionCodingMessage + '\n\n') : '') + confirmMessage;

    const confirmed = await base.showConfirmationDialog('Apply Admission Coding?', combinedMessage);

    // If confirmed, save the codes for each date
    if (confirmed) {
      if (checkAdmissionTypeCoding) {
        await this.saveMissedProcedureCodes(patient, [admissionMoment.format('DD/MM/YYYY')], admissionCodes, base);
      }
      await this.saveMissedProcedureCodes(patient, filteredDatesList, procedureCodes, base);
      return true;
    }

    return false;
  }

  async checkMissingDischargeCoding(
    patient: any,
    formValues: any,
    hospitals: any[],
    patientProcedureCodes: any[] | null,
    base: BaseComponent
  ): Promise<boolean> {
    const { dischargeDate, hospitalId, wardId } = formValues;

    // Only continue if the selected ward has auto coding codes
    const selectedHospital = hospitals.find((hospital: any) => hospital.id === hospitalId);
    if (!selectedHospital) {
      return false;
    }

    const selectedWard = selectedHospital.wards.find((ward: any) => ward.id === wardId);
    if (!selectedWard || !selectedWard.procedureCodes || selectedWard.procedureCodes.length === 0) {
      return false;
    }

    const procedureCodes = selectedWard.procedureCodes;
    const dischargeMoment = moment.utc(dischargeDate).startOf('day');
    const dischargeDateString = dischargeMoment.format('DD/MM/YYYY');

    // Check if codes exist for the discharge date
    const codesExist = procedureCodes.some((code: any) =>
      this.doesCodeExistForDate(code.code, dischargeDateString, patientProcedureCodes)
    );

    // If codes exist, no need to do anything
    if (codesExist) {
      return false;
    }

    // If codes do not exist, show dialog and allow user to apply missing codes
    const confirmMessage = `Do you want to apply the following ward codes to the discharge date?\nCodes: ${procedureCodes.map((c: any) => c.code).join(', ')}\nDate: ${dischargeDateString}`;
    const confirmed = await base.showConfirmationDialog('Apply Discharge Coding?', confirmMessage);

    // If confirmed, save the codes for the discharge date
    if (confirmed) {
      await this.saveMissedProcedureCodes(patient, [dischargeDateString], procedureCodes, base);
      return true;
    }

    return false;
  }

  private async saveMissedProcedureCodes(
    patient: any,
    datesList: string[],
    procedureCodes: any[],
    base: BaseComponent
  ): Promise<void> {
    const requests = datesList.map(dateString => {
      const date = moment.utc(dateString, 'DD/MM/YYYY');
      const codeGroupData = {
        date: date.toISOString(),
        groupId: patient.groupId,
        patientId: patient.id,
        procedureCodes,
        paidCash: false,
        noCharge: false,
        providerId: null, // Default to null for auto-generated codes
      };
      return this.saveCodeGroup(codeGroupData);
    });

    return forkJoin(requests).toPromise()
      .then(() => {
        base.showToastMessage('Patient procedure codes saved successfully');
      })
      .catch((error) => {
        console.error('Error saving patient procedure codes: ', error);
        base.showToastMessage('There was an error saving the patient procedure codes. Please try again.');
      });
  }

  async checkOverCoding(
    formValues: any,
    hospitals: any[],
    patient: any,
    patientProcedureCodes: any[] | null,
    base: BaseComponent
  ): Promise<boolean> {
    const { startDate, endDate } = formValues;

    const startMoment = moment.utc(startDate).startOf('day');
    const endMoment = moment.utc(endDate).startOf('day').add(1, 'days'); // Include the end date in the range
    const codesToRemove: any[] = [];

    const selectedHospital = hospitals.find((hospital: any) => hospital.id === patient.hospitalId);
    if (!selectedHospital) {
      return false;
    }

    const selectedWard = selectedHospital.wards.find((ward: any) => ward.id === patient.wardId);
    if (!selectedWard || !selectedWard.procedureCodes || selectedWard.procedureCodes.length === 0) {
      return false;
    }

    const procedureCodes = selectedWard.procedureCodes.map((code: any) => code.code);

    for (let m = startMoment.clone(); m.isSameOrBefore(endMoment); m.add(1, 'days')) {
      const dateStr = m.format('YYYY-MM-DD');

      const matchingCodes = patientProcedureCodes?.filter((code: any) =>
        procedureCodes.includes(code.code) && moment.utc(code.date).isSame(m, 'day')
      );

      if (matchingCodes && matchingCodes.length > 0) {
        codesToRemove.push(...matchingCodes);
      }
    }

    if (codesToRemove.length === 0) {
      return false;
    }

    const confirmMessage = `This patient has the following codes that potentially need to be removed in order to prevent over-coding. Do you want to remove them?\nCodes: ${codesToRemove.map((c: any) => c.code).join(', ')}`;
    const confirmed = await base.showConfirmationDialog('Remove Auto Coding Procedure Codes', confirmMessage);

    // If confirmed, remove the codes
    if (confirmed) {
      await this.removeProcedureCodes(codesToRemove, base);
      return true;
    }

    return false;
  }


  private async removeProcedureCodes(codesToRemove: any[], base: BaseComponent): Promise<void> {
    const requests = codesToRemove.map(code => {
      return this.deleteCode(code.id);
    });

    return forkJoin(requests).toPromise()
      .then(() => {
        base.showToastMessage('Patient procedure codes removed successfully');
      })
      .catch((error) => {
        console.error('Error removing patient procedure codes: ', error);
        base.showToastMessage('There was an error removing the patient procedure codes. Please try again.');
      });
  }
}
